/**
 * Suivi d'une livraison, côté client.
 *
 * Interrogation périodique d'une route plutôt que canal permanent : le canal
 * socket.io qui tenait ce rôle n'était alimenté par personne. Le client lisait
 * « Connecté — suivi en temps réel actif » devant une carte définitivement
 * vide, et rien ne le lui disait. Une route qui répond ne peut pas se
 * désynchroniser en silence.
 *
 * La cadence suit ce qu'il y a à voir, et la page se tait dès qu'on la quitte :
 * ce suivi se regarde en 4G, sur un téléphone posé sur un plan de travail.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api';
import { logger } from '../utils/logger';

/** Le livreur roule : on rafraîchit assez souvent pour que la carte bouge. */
const INTERVALLE_EN_ROUTE_MS = 20_000;
/** Rien ne bouge encore (ou plus) : une minute suffit largement. */
const INTERVALLE_AU_REPOS_MS = 60_000;

export type DeliveryTrackingState = 'not_scheduled' | 'scheduled' | 'en_route' | 'delivered';

export interface GeoPoint {
  lat: number;
  lng: number;
}

export interface DeliveryTracking {
  order_id: string;
  order_number: string;
  state: DeliveryTrackingState;
  /** Présent uniquement quand `state === 'not_scheduled'`. */
  message?: string;
  delivery_status?: string;
  stop_status?: string;
  /** Heure de passage estimée (ISO). */
  estimated_time?: string | null;
  /** Heure de passage réelle (ISO), une fois l'arrêt fait. */
  actual_time?: string | null;
  /** Nombre d'arrêts restant avant le sien. */
  stops_before?: number;
  driver_position?: GeoPoint | null;
  driver_position_at?: string | null;
  destination?: GeoPoint | null;
  distance_km?: number | null;
  eta_minutes?: number | null;
  proof?: {
    recipient_name?: string | null;
    notes?: string | null;
    delivered_at?: string | null;
  } | null;
  incident?: { type: string; created_at: string } | null;
}

export function useDeliveryTracking(orderId: string | null) {
  const [tracking, setTracking] = useState<DeliveryTracking | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(orderId));
  const [error, setError] = useState<string | null>(null);

  // L'état reçu pilote la cadence de la prochaine interrogation. Il vit dans une
  // référence et non dans le state : la boucle ne doit pas se relancer à chaque
  // rendu, sinon deux minuteries se superposent et la cadence double.
  const etatRef = useRef<DeliveryTrackingState | null>(null);
  const timerRef = useRef<number | null>(null);
  const annuleRef = useRef(false);

  const arreterTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const interroger = useRef<() => void>(() => {});

  useEffect(() => {
    annuleRef.current = false;

    if (!orderId) {
      setTracking(null);
      setLoading(false);
      return;
    }

    const planifier = () => {
      if (annuleRef.current) return;
      // Livrée : plus rien ne changera, on s'arrête pour de bon.
      if (etatRef.current === 'delivered') return;
      // Onglet masqué : la reprise est déclenchée par `visibilitychange`.
      if (document.visibilityState === 'hidden') return;

      arreterTimer();
      timerRef.current = window.setTimeout(
        () => interroger.current(),
        etatRef.current === 'en_route' ? INTERVALLE_EN_ROUTE_MS : INTERVALLE_AU_REPOS_MS,
      );
    };

    const lancer = async () => {
      if (annuleRef.current || document.visibilityState === 'hidden') return;

      try {
        const data = await api.get<DeliveryTracking>(`/delivery-tracking/orders/${orderId}`);
        if (annuleRef.current) return;
        etatRef.current = data.state;
        setTracking(data);
        setError(null);
      } catch (err: any) {
        if (annuleRef.current) return;
        logger.error('[useDeliveryTracking] Échec de l’interrogation:', err);
        // Un suivi qui ne répond pas n'invalide pas ce qui est déjà affiché :
        // on garde la dernière position connue et on signale sobrement.
        setError(
          err?.response?.data?.message || 'Suivi momentanément indisponible. Nouvelle tentative en cours…',
        );
      } finally {
        if (!annuleRef.current) {
          setLoading(false);
          planifier();
        }
      }
    };

    interroger.current = lancer;
    void lancer();

    const surVisibilite = () => {
      if (document.visibilityState === 'hidden') {
        arreterTimer();
      } else {
        // De retour sur la page : ce qui est affiché a vieilli, on redemande
        // tout de suite plutôt que d'attendre le prochain tour.
        void lancer();
      }
    };

    document.addEventListener('visibilitychange', surVisibilite);

    return () => {
      annuleRef.current = true;
      arreterTimer();
      document.removeEventListener('visibilitychange', surVisibilite);
    };
  }, [orderId, arreterTimer]);

  /** Rafraîchissement à la demande (bouton « Actualiser »). */
  const refresh = useCallback(() => {
    interroger.current();
  }, []);

  return { tracking, loading, error, refresh };
}
